import {
  GridTable,
  GridTableColumn,
  GridTableRecord,
} from "@apex/component-library";
import { useLocalStorage } from "ui-classic-common-library";

import {
  RequirementsDetails,
  RequirementsResponse,
} from "../../../api/useGetRequirements";
import { csvExportAndDownload } from "../../../utils/csvExportAndDownload";
import getDateString from "../../../utils/getDateString";
import { getEnumAsFilterOptions } from "../../CallsAlertsResultsGrid/gridUtils";
import { getCurrencyValue, marginProcessingExportFileName } from "../auxUtils";
import { SectionLoadingState } from "../RenderState";
import { RequirementTypes } from "./types";

interface TradeDetailsTableProps {
  data?: RequirementsResponse;
  isLoading: boolean;
}

const TradeDetailsTable = ({ data, isLoading }: TradeDetailsTableProps) => {
  const defaultColumn = {
    isSortable: true,
    width: 140,
    isFilterable: true,
    isVisibleByDefault: true,
  };
  const tradeDetailsColumns: GridTableColumn[] = [
    {
      ...defaultColumn,
      key: "tradeType",
      label: "Trade Type",
      recordField: "type",
      options: getEnumAsFilterOptions(RequirementTypes),
    },
    {
      ...defaultColumn,
      key: "accountType",
      label: "Account Type",
      recordField: "accountType",
    },
    {
      ...defaultColumn,
      key: "orderId",
      label: "Order ID",
      recordField: "orderId",
    },
    {
      ...defaultColumn,
      key: "tradeNumber",
      label: "Trade Number",
      recordField: "tradeNumber",
    },
    {
      ...defaultColumn,
      key: "tradeDate",
      label: "Trade Date",
      recordField: "tradeDate",
      filterByDate: true,
    },
    {
      ...defaultColumn,
      key: "executionTime",
      label: "Execution Time",
      recordField: "executionTime",
    },
    {
      ...defaultColumn,
      key: "symbol",
      label: "Symbol",
      recordField: "symbol",
    },
    {
      ...defaultColumn,
      key: "shareQuantity",
      label: "Share Quantity",
      recordField: "shareQuantity",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "netAmount",
      label: "Net Amount",
      recordField: "netAmount",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "sharePrice",
      label: "Share Price",
      recordField: "sharePrice",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "currencyCode",
      label: "Curr. Code",
      recordField: "currencyCode",
    },
    {
      ...defaultColumn,
      key: "requirement",
      label: "Requirement",
      recordField: "requirement",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "requirementType",
      label: "Requirement Type",
      recordField: "requirementType",
    },
    {
      ...defaultColumn,
      key: "underlyingSymbolClass",
      label: "Class",
      recordField: "underlyingSymbolClass",
    },
    {
      ...defaultColumn,
      key: "unavailableBalance",
      label: "Unavailable Balance",
      recordField: "unavailableBalance",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "buySellCode",
      label: "Open or Close",
      recordField: "buySellCode",
    },
    {
      ...defaultColumn,
      key: "preMeet",
      label: "Pre Meet",
      recordField: "preMeet",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "highWaterMark",
      label: "High Water Mark",
      recordField: "highWaterMark",
      alignment: "right",
    },
    {
      ...defaultColumn,
      key: "runningOpenPosition",
      label: "Running Open Position",
      recordField: "runningOpenPosition",
      alignment: "right",
    },
  ];
  const defaultVisibleOptions: string[] = [
    "runningOpenPosition",
    "type",
    "accountType",
    "orderId",
    "tradeNumber",
    "tradeDate",
    "executionTime",
    "symbol",
    "shareQuantity",
    "netAmount",
    "sharePrice",
    "currencyCode",
    "requirement",
    "requirementType",
    "underlyingSymbolClass",
    "unavailableBalance",
    "buySellCode",
    "preMeet",
    "highWaterMark",
  ];
  const [visibleColumns, setVisibleColumns] = useLocalStorage(
    "MARGIN_PROCESSING",
    "requirements-and-activity-by-details",
    Array.from(defaultVisibleOptions)
  );
  const tradeDetailsData = (): GridTableRecord[] => {
    if (data) {
      return data?.requirementsDetails.map(
        (data: RequirementsDetails, index) => {
          const {
            type,
            accountType,
            orderID,
            tradeNumber,
            tradeDate,
            executionTime,
            symbol,
            shareQuantity,
            netAmount,
            sharePrice,
            currencyCode,
            requirement,
            requirementType,
            underlyingSymbolClass,
            unavailableBalance,
            buySellCode,
            preMeet,
            highWaterMark,
            runningOpenPosition,
          } = data;
          return {
            key: `details-${index}`,
            type,
            accountType,
            orderId: orderID,
            tradeNumber,
            tradeDate: getDateString(tradeDate),
            executionTime,
            symbol,
            shareQuantity,
            netAmount: getCurrencyValue(netAmount),
            sharePrice: getCurrencyValue(sharePrice),
            currencyCode,
            requirement: getCurrencyValue(requirement),
            requirementType,
            underlyingSymbolClass,
            unavailableBalance: getCurrencyValue(unavailableBalance),
            buySellCode,
            preMeet: preMeet ? getCurrencyValue(preMeet) : "",
            highWaterMark,
            runningOpenPosition: getCurrencyValue(runningOpenPosition),
          };
        }
      );
    }
    return [];
  };

  return (
    <>
      {isLoading && <SectionLoadingState />}
      {!isLoading && (
        <GridTable
          columns={tradeDetailsColumns}
          records={tradeDetailsData()}
          initialVisibleColumns={new Set(visibleColumns)}
          onChangeVisibleColumns={updatedColumns =>
            setVisibleColumns(Array.from(updatedColumns))
          }
          hasFooter={false}
          tableBorder={false}
          minHeight="100px"
          exportFileName={marginProcessingExportFileName()}
          onExportClick={csvExportAndDownload}
          data-testid="trade-details-table"
        />
      )}
    </>
  );
};

export default TradeDetailsTable;
